import React, { useState, useEffect } from 'react';
import {
  Card, Table, Button, Space, Input, Select, Tag, message, Row, Col, Statistic, Modal,
} from 'antd';
import { ReloadOutlined, EyeOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import * as smsService from '../../services/smsService';

const SmsLog: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [logs, setLogs] = useState<smsService.SmsLog[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchPhone, setSearchPhone] = useState('');
  const [searchStatus, setSearchStatus] = useState<string>('');
  const [stats, setStats] = useState({ totalCount: 0, successCount: 0, failCount: 0, pendingCount: 0 });
  const [detailVisible, setDetailVisible] = useState(false);
  const [currentLog, setCurrentLog] = useState<smsService.SmsLog | null>(null);

  const loadLogs = async () => {
    setLoading(true);
    try {
      const res = await smsService.listLogs({
        page, pageSize,
        phone: searchPhone || undefined,
        sendStatus: searchStatus || undefined,
      });
      setLogs(res.data.list || []);
      setTotal(res.data.total || 0);
    } catch {
      message.error('加载日志列表失败');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const res = await smsService.getStats();
      setStats(res.data);
    } catch { }
  };

  useEffect(() => { loadLogs(); loadStats(); }, [page, pageSize]);

  const handleSearch = () => { setPage(1); loadLogs(); };
  const handleReset = () => { setSearchPhone(''); setSearchStatus(''); setPage(1); loadLogs(); };

  const handleViewDetail = (record: smsService.SmsLog) => {
    setCurrentLog(record);
    setDetailVisible(true);
  };

  const getStatusTag = (status: number) => {
    const opt = smsService.sendStatusOptions.find(o => o.value === status);
    return <Tag color={opt?.color || 'default'}>{opt?.label || status}</Tag>;
  };

  const columns: ColumnsType<smsService.SmsLog> = [
    { title: '手机号', dataIndex: 'phone', key: 'phone', width: 120 },
    { title: '模板', dataIndex: 'templateName', key: 'templateName', width: 100 },
    { title: '渠道', dataIndex: 'channelName', key: 'channelName', width: 80 },
    { title: '短信内容', dataIndex: 'content', key: 'content', ellipsis: true },
    { title: '发送状态', dataIndex: 'sendStatus', key: 'sendStatus', width: 90, render: getStatusTag },
    { title: '返回信息', dataIndex: 'resultMsg', key: 'resultMsg', width: 120, ellipsis: true },
    {
      title: '发送时间', dataIndex: 'createdAt', key: 'createdAt', width: 160,
      render: (t) => t?.replace('T', ' ').substring(0, 19),
    },
    {
      title: '操作', key: 'action', width: 80,
      render: (_, record) => (
        <Button type="link" size="small" icon={<EyeOutlined />} onClick={() => handleViewDetail(record)}>详情</Button>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={6}>
          <Card><Statistic title="总发送量" value={stats.totalCount} suffix="条" /></Card>
        </Col>
        <Col span={6}>
          <Card><Statistic title="发送成功" value={stats.successCount} suffix="条" valueStyle={{ color: '#52c41a' }} /></Card>
        </Col>
        <Col span={6}>
          <Card><Statistic title="发送失败" value={stats.failCount} suffix="条" valueStyle={{ color: '#ff4d4f' }} /></Card>
        </Col>
        <Col span={6}>
          <Card><Statistic title="待发送" value={stats.pendingCount} suffix="条" valueStyle={{ color: '#1890ff' }} /></Card>
        </Col>
      </Row>

      <Card
        title="短信日志"
        extra={<Button icon={<ReloadOutlined />} onClick={() => { loadLogs(); loadStats(); }}>刷新</Button>}
      >
        <Space style={{ marginBottom: 16 }}>
          <Input placeholder="手机号" value={searchPhone} onChange={(e) => setSearchPhone(e.target.value)} style={{ width: 150 }} allowClear />
          <Select placeholder="发送状态" value={searchStatus || undefined} onChange={setSearchStatus} style={{ width: 120 }} allowClear>
            {smsService.sendStatusOptions.map(o => <Select.Option key={o.value} value={o.value}>{o.label}</Select.Option>)}
          </Select>
          <Button type="primary" onClick={handleSearch}>搜索</Button>
          <Button onClick={handleReset}>重置</Button>
        </Space>
        <Table columns={columns} dataSource={logs} rowKey="id" loading={loading}
          pagination={{ current: page, pageSize, total, showSizeChanger: true, showTotal: (t) => `共 ${t} 条`,
            onChange: (p, ps) => { setPage(p); setPageSize(ps); } }} />
      </Card>

      <Modal title="短信详情" open={detailVisible} onCancel={() => setDetailVisible(false)} footer={null} width={600}>
        {currentLog && (
          <div style={{ lineHeight: 2 }}>
            <p><strong>手机号：</strong>{currentLog.phone}</p>
            <p><strong>渠道：</strong>{currentLog.channelName}</p>
            <p><strong>模板：</strong>{currentLog.templateName} ({currentLog.templateCode})</p>
            <p><strong>短信内容：</strong>{currentLog.content}</p>
            <p><strong>参数：</strong>{currentLog.params}</p>
            <p><strong>发送状态：</strong>{getStatusTag(currentLog.sendStatus)}</p>
            <p><strong>返回码：</strong>{currentLog.resultCode}</p>
            <p><strong>返回信息：</strong>{currentLog.resultMsg}</p>
            <p><strong>业务ID：</strong>{currentLog.bizId}</p>
            <p><strong>请求IP：</strong>{currentLog.ip}</p>
            <p><strong>创建时间：</strong>{currentLog.createdAt?.replace('T', ' ').substring(0, 19)}</p>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default SmsLog;
